'use client'

import { chatPrefillMark } from '@/app/lib/guideChat/prefill'
import type { CollectedFieldName } from '@/app/lib/guideChat/types'

import { forwardRef, useCallback, useImperativeHandle, useEffect, useMemo, useState, useRef } from 'react'
import { useForm, Controller, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@/app/lib/zodResolver'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import {
    loanFormSchema,
    LoanFormData,
    createLoanFormDefaults,
    DELAY_TYPES,
    CURRENCIES,
    currencySymbol,
    LOAN_VALIDATION,
    LOAN_TYPES,
    loanHasStages,
    showDelayFields,
    isInFineLoan,
    isRelayLoan,
    getMaxDuration,
    formatDurationToYearsAndMonths,
} from '@/app/schemas/loan-comparator/loan.schema'
import { isNewLoan as checkIsNewLoan, LOAN_INFORMATION_TYPES } from '@/app/constants/loanTypes'
import LoanComparatorPalierForm from './LoanComparatorPalierForm'
import LoanFormField from '@/app/components/loanComparator/fields/LoanFormField'
import LoanImportedBadge from '@/app/components/loanComparator/pdfExtract/LoanImportedBadge'
import { useLoanPdfImport } from '@/app/contexts/loan-pdf-import-context'
import { loanFieldKey, LOAN_IMPORT_FIELDS } from '@/app/utils/loan/loan-pdf-extract-merge'
import AppFormInput from '@/app/components/atomic/AppFormInput'
import AppSelect from '@/app/components/atomic/AppSelect'
import YesNoSwitch from '@/app/components/atomic/molecules/YesNoSwitch'
import DescriptiveToggle, { DescriptiveToggleOption } from '@/app/components/atomic/molecules/DescriptiveToggle'
import {
    computeInitialDeferral,
    computeLoanMonthsLeft,
    computeLoanSumLeft,
    computeRemainingDeferral,
    timeOffsetFromLoanForm,
} from '@/app/utils/loanCalculations'
import { resolveLoanBounds } from '@/app/utils/loan/loan-insured-amount'
import AppNumericInput from '@/app/components/atomic/Input/AppNumericInput'

// Champs du formulaire dont la correction manuelle lève le marqueur d'un autre champ importé
const IMPORT_FIELD_ALIASES: Record<string, string> = {
    delay_type: 'delay',
    delay_duration: 'delay',
    has_delay: 'delay',
    amount_left: 'amount',
    duration_left: 'duration',
}

// Parse the formatted string ("100 000", "3,50") returned by AppNumericInput into a
// number. Empty parses to null, NOT undefined: with undefined RHF falls back to defaultValues,
// so a cleared field would silently redisplay its previous value (schema maps null → empty).
const parseFormattedAmount = (value: string | number | null | undefined): number | null => {
    const n = parseFloat(
        String(value ?? '')
            .replace(/\s/g, '')
            .replace(',', '.')
    )
    return Number.isNaN(n) ? null : n
}

// Exposed methods interface
export interface LoanFormHandle {
    validate: () => Promise<boolean>
    getData: () => LoanFormData
    reset: () => void
    getErrorMessages: () => string[]
    collapse: () => void
}

interface Props {
    loanId?: number
    loanIndex?: number
    className?: string
    onRemove?: () => void
    canRemove?: boolean
    // Dernière ligne dépliée par défaut, les précédentes repliées (maquette)
    isLast?: boolean
    externalAmountError?: string | null
}

// Switch €/CHF dans l'input montant (remplace l'ancien dropdown de monnaie, maquette étape 3)
function CurrencySwitch({ value, onChange }: { value: string; onChange: (unit: string) => void }) {
    const isChf = value === CURRENCIES.CHF
    return (
        <div className="self-stretch flex items-center gap-1.5 text-[14px] font-semibold">
            <span className={isChf ? 'text-rm-gray' : 'text-blue-500'}>€</span>
            <button
                type="button"
                role="switch"
                aria-checked={isChf}
                aria-label="Devise du montant : euros ou francs suisses"
                data-testid="loan-currency-selector"
                data-currency={value}
                onClick={() => onChange(isChf ? CURRENCIES.EUR : CURRENCIES.CHF)}
                className="relative h-5 w-9 rounded-full bg-rm-gray-light cursor-pointer transition-colors hover:bg-rm-gray"
            >
                <span
                    className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        isChf ? 'translate-x-4' : ''
                    }`}
                />
            </button>
            <span className={isChf ? 'text-blue-500' : 'text-rm-gray'}>CHF</span>
        </div>
    )
}

const LoanComparatorLoanForm = forwardRef<LoanFormHandle, Props>(
    (
        { loanId, loanIndex = 0, className = '', onRemove, canRemove = false, isLast = true, externalAmountError },
        ref
    ) => {
        // Store state and actions
        const quote = useLoanComparatorStore((state) => state.quote)
        const formOptions = useLoanComparatorStore((state) => state.formOptions)
        const globalOptions = useLoanComparatorStore((state) => state.globalOptions)
        const loanActions = useLoanComparatorStore((state) => state.loan)

        // Determine if this is a new loan
        const isNewLoan = useMemo(() => checkIsNewLoan(quote?.type_quote), [quote?.type_quote])

        // Get loan data from store
        const loanData = useMemo(() => {
            if (loanId !== undefined) {
                return quote?.loans?.find((l) => l.id === loanId)
            }
            return quote?.loans?.[loanIndex]
        }, [quote?.loans, loanId, loanIndex])

        // Le mode « capital restant » n'est jamais proposé sur un nouveau prêt (parité legacy Vue)
        const initialShowLeftAmount = useMemo(
            () => loanData?.information_type === LOAN_INFORMATION_TYPES.SUM_LEFT && !isNewLoan,
            [loanData?.information_type, isNewLoan]
        )
        const { isImported, clearImported } = useLoanPdfImport()
        const isFieldImported = (field: string) => isImported(loanFieldKey(loanIndex, field))
        const prefilledByChat = useLoanComparatorStore((state) => state.prefilledByChat)
        const chatMark = (name: CollectedFieldName) => (loanIndex === 0 ? chatPrefillMark(prefilledByChat, name) : null)
        const hasImportedField = LOAN_IMPORT_FIELDS.some((field) => isImported(loanFieldKey(loanIndex, field)))

        // Après un import on déplie la première ligne ; ajoutée à la main, c'est la dernière
        const [collapsed, setCollapsed] = useState(() => (hasImportedField ? loanIndex > 0 : !isLast))

        // Référentiel des données « restantes » : la date de signature, synchronisée sur le 1er prêt par le formulaire financement
        const firstLoan = quote?.loans?.[0]
        const signatureDate = useMemo(() => {
            return firstLoan?.new_date_loan || quote?.date_contract_begin || null
        }, [firstLoan?.new_date_loan, quote?.date_contract_begin])

        // Map loan data to form defaults
        const initialValues = useMemo((): Partial<LoanFormData> => {
            if (!loanData) return createLoanFormDefaults()

            // sum and sum_left hold the euro conversion: redisplay the amounts as they were typed.
            const isAltCurrencyLoan = loanData.sum_alt_currency != null || loanData.sum_left_alt_currency != null

            // Le back stocke le différé depuis la signature ; en capital restant le formulaire affiche le différé restant à courir (RM-920)
            const displayedDeferral = initialShowLeftAmount
                ? computeRemainingDeferral(loanData.time_offset || 0, signatureDate)
                : loanData.time_offset || 0

            const values = {
                type: loanData.type_loan?.id || LOAN_TYPES.CLASSIC,
                amount: (loanData.sum_alt_currency ?? loanData.sum) || undefined,
                amount_left: (loanData.sum_left_alt_currency ?? loanData.sum_left) || undefined,
                duration: loanData.period || undefined,
                duration_left: loanData.month_left || undefined,
                rate: loanData.rate ?? undefined,
                has_delay: displayedDeferral > 0,
                delay_duration: displayedDeferral || undefined,
                delay_type: loanData.partial_time_offset ? DELAY_TYPES.PARTIAL : DELAY_TYPES.TOTAL,
                stages: loanData.palier || [],
                selected_currency: isAltCurrencyLoan ? CURRENCIES.CHF : CURRENCIES.EUR,
                save_amount_left: initialShowLeftAmount,
            }

            return values
        }, [initialShowLeftAmount, loanData, loanId, loanIndex, signatureDate])

        const formatErrors = (err: FieldErrors): string[] => {
            return Object.entries(err).flatMap(([key, value]) => {
                if (!value) return []
                if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
                    return [`${key}: ${value.message}`]
                }
                if (typeof value === 'object') {
                    return formatErrors(value as FieldErrors).map((child) => `${key}.${child}`)
                }
                return []
            })
        }

        // Setup React Hook Form
        const {
            register,
            control,
            formState: { errors },
            watch,
            trigger,
            getValues,
            reset: resetForm,
            setValue,
        } = useForm<LoanFormData>({
            resolver: zodResolver(loanFormSchema),
            mode: 'onBlur',
            reValidateMode: 'onChange',
            defaultValues: initialValues,
        })

        // Expose methods to parent
        useImperativeHandle(ref, () => ({
            validate: async () => {
                return await trigger()
            },
            getData: () => {
                return getValues()
            },
            reset: () => {
                resetForm(createLoanFormDefaults())
            },
            getErrorMessages: () => {
                return formatErrors(errors)
            },
            collapse: () => {
                setCollapsed(true)
            },
        }))

        // Identity only — keying values here retriggers reset every keystroke (watch→store) and wipes validation.
        // The chat prefill lands after mount and never changes the loan id, so its marks are part of the identity.
        const chatPrefillKey = loanIndex === 0 ? prefilledByChat.join(',') : ''
        const loanDataString = JSON.stringify({
            quoteId: quote?.id,
            id: loanData?.id,
            chatPrefillKey,
        })
        const prevDataStringRef = useRef<string | undefined>(undefined)

        useEffect(() => {
            const hasLoanData = !!loanData?.id || (loanData && (loanData.sum || loanData.period))
            const dataChanged = loanDataString !== prevDataStringRef.current

            if (hasLoanData && dataChanged) {
                resetForm(initialValues)
                prevDataStringRef.current = loanDataString
            }
        }, [loanDataString, loanData, quote?.id, initialValues, resetForm])

        // Watch fields for conditional rendering
        const loanType = watch('type')
        const hasDelay = watch('has_delay')
        const duration = watch('duration')
        const durationLeft = watch('duration_left')
        const delayDuration = watch('delay_duration')
        const amount = watch('amount')
        const amountLeft = watch('amount_left')
        const rate = watch('rate')
        const delayType = watch('delay_type')
        const selectedCurrency = watch('selected_currency') || CURRENCIES.EUR
        // Seule source de vérité du mode : un état local dupliqué se lit périmé dans l'effet de dérivation
        const showLeftAmount = watch('save_amount_left') ?? false

        const activeDuration = showLeftAmount ? durationLeft : duration

        // The amount error comes from the parent: without it a collapsed line blocks the wizard with nothing to show
        const hasErrors = Object.keys(errors).length > 0 || !!externalAmountError
        useEffect(() => {
            if (hasErrors) setCollapsed(false)
        }, [hasErrors])

        // Sync form changes to Zustand store
        useEffect(() => {
            const subscription = watch((formData, { name, type }) => {
                // Corrigé à la main, le champ n'est plus « importé » : seule une saisie porte type 'change'
                if (type === 'change' && name) {
                    clearImported(loanFieldKey(loanIndex, IMPORT_FIELD_ALIASES[name] ?? name))
                }

                if (loanData?.id !== undefined) {
                    const typeName = formOptions?.loanTypeList?.find((lt) => lt.id === formData.type)?.name
                    loanActions.update(loanData.id, {
                        sum: formData.amount,
                        sum_left: formData.amount_left,
                        period: formData.duration,
                        month_left: formData.duration_left,
                        rate: formData.rate,
                        time_offset: timeOffsetFromLoanForm(formData, signatureDate),
                        partial_time_offset: formData.delay_type === DELAY_TYPES.PARTIAL,
                        selected_currency: formData.selected_currency,
                        // L'éligibilité Lemoine lit le mode dans le store : sans cette sync le switch reste invisible
                        information_type: formData.save_amount_left
                            ? LOAN_INFORMATION_TYPES.SUM_LEFT
                            : LOAN_INFORMATION_TYPES.SUM_INITIAL,
                        // Les bornes de montant se lisent par libellé de type : sans cette sync le parent garde celui de l'hydratation
                        ...(formData.type && typeName ? { type_loan: { id: formData.type, name: typeName } } : {}),
                    })
                }
            })
            return () => subscription.unsubscribe()
        }, [watch, loanData, loanActions, formOptions?.loanTypeList, clearImported, loanIndex, signatureDate])

        // Re-validate the duration when the loan type changes: the per-type cap defines its max,
        // but changing the type does not otherwise re-run the duration field's validation.
        useEffect(() => {
            const field = showLeftAmount ? 'duration_left' : 'duration'
            const value = getValues(field)
            // NaN = empty valueAsNumber input (e.g. right after reset): triggering would flag an untouched field
            if (value != null && !Number.isNaN(value)) trigger(field)
        }, [loanType, showLeftAmount, getValues, trigger])

        // Build loan type options from formOptions
        const loanTypeOptions = useMemo(() => {
            if (!formOptions?.loanTypeList) return []
            return formOptions.loanTypeList.map((lt) => ({
                label: lt.name,
                value: lt.id,
            }))
        }, [formOptions?.loanTypeList])

        // Get current loan type name for validation lookup
        const currentLoanTypeName = useMemo(() => {
            const selectedOption = loanTypeOptions.find((opt) => opt.value === loanType)
            return selectedOption?.label || 'default'
        }, [loanType, loanTypeOptions])

        // Get validation rules from globalOptions based on loan type
        const loanValidation = useMemo(() => {
            const defaultValidation = {
                amount: { min: LOAN_VALIDATION.MIN_AMOUNT, max: LOAN_VALIDATION.MAX_AMOUNT },
                duration: { min: LOAN_VALIDATION.MIN_DURATION, max: LOAN_VALIDATION.MAX_DURATION_CLASSIC },
            }

            const base = resolveLoanBounds(globalOptions, currentLoanTypeName) || defaultValidation

            // Cap in fine / relais at their per-type maximum regardless of the backend label match
            const max =
                isInFineLoan(loanType) || isRelayLoan(loanType)
                    ? Math.min(base.duration.max, getMaxDuration(loanType))
                    : base.duration.max

            return { ...base, duration: { ...base.duration, max } }
        }, [globalOptions, currentLoanTypeName, loanType])

        // Delay options: none / partial / total (single descriptive toggle)
        const delayToggleOptions: DescriptiveToggleOption<
            'none' | typeof DELAY_TYPES.PARTIAL | typeof DELAY_TYPES.TOTAL
        >[] = [
            { value: 'none', title: 'Pas de différé', description: 'Remboursement dès le 1er mois' },
            {
                value: DELAY_TYPES.PARTIAL,
                title: 'Différé partiel',
                description: 'Vous remboursez les intérêts seulement',
            },
            {
                value: DELAY_TYPES.TOTAL,
                title: 'Différé total',
                description: 'Aucun remboursement pendant la période',
            },
        ]

        // Intérêts remboursés (in fine / relais) : même mécanique que le différé, wordings maquette
        const interestsToggleOptions: DescriptiveToggleOption<
            'none' | typeof DELAY_TYPES.PARTIAL | typeof DELAY_TYPES.TOTAL
        >[] = [
            {
                value: 'none',
                title: "Pas d'intérêts remboursés",
                description: 'Vous remboursez le capital et les intérêts dès le 1er mois',
            },
            {
                value: DELAY_TYPES.PARTIAL,
                title: 'Intérêts remboursés partiellement',
                description: 'Vous remboursez uniquement les intérêts pendant la période',
            },
            {
                value: DELAY_TYPES.TOTAL,
                title: 'Intérêts remboursés totalement',
                description: 'Aucun remboursement du capital ni des intérêts pendant la période',
            },
        ]

        // Le différé saisi change de référentiel avec le mode : depuis la signature (capital initial) <-> restant à courir (capital restant)
        const setAmountFieldMode = (next: boolean) => {
            if (next !== showLeftAmount) {
                const typedDeferral = getValues('delay_duration')
                if (getValues('has_delay') && typedDeferral) {
                    const convertedDeferral = next
                        ? computeRemainingDeferral(typedDeferral, signatureDate)
                        : computeInitialDeferral(typedDeferral, signatureDate)
                    setValue('delay_duration', convertedDeferral || undefined)
                    if (!convertedDeferral) setValue('has_delay', false)
                }
            }
            setValue('save_amount_left', next)
        }

        // Callback stable : le résumé replié observe stages, un callback inline relancerait le report en boucle
        const handleStagesChange = useCallback(
            (nextStages: { amount: number | undefined; month_duration: number | undefined }[]) => {
                // setValue n'émet pas de type 'change' : le marqueur d'import se nettoie ici
                clearImported(loanFieldKey(loanIndex, 'stages'))
                setValue('stages', nextStages, { shouldDirty: false })
            },
            [setValue, clearImported, loanIndex]
        )

        // Legacy auto-computation of duration_left & amount_left
        useEffect(() => {
            // Mode relu dans le formulaire : sur hydratation tardive la closure écraserait la saisie
            if (getValues('save_amount_left') || isNewLoan) return
            if (!signatureDate || !duration || !amount) return

            const monthsLeft = computeLoanMonthsLeft(signatureDate, duration, 0)
            if (monthsLeft !== null) {
                setValue('duration_left', monthsLeft, { shouldValidate: false, shouldDirty: false })

                const capitalLeft = computeLoanSumLeft(duration, amount, rate || 0, delayDuration || 0, monthsLeft)
                setValue('amount_left', capitalLeft, { shouldValidate: false, shouldDirty: false })
            }
        }, [amount, delayDuration, duration, getValues, isNewLoan, rate, setValue, showLeftAmount, signatureDate])

        // Track previous loan type to detect actual changes
        const prevLoanTypeRef = useRef<number | undefined>(undefined)

        useEffect(() => {
            const typeActuallyChanged = prevLoanTypeRef.current !== undefined && prevLoanTypeRef.current !== loanType

            prevLoanTypeRef.current = loanType

            if (typeActuallyChanged && (isInFineLoan(loanType) || isRelayLoan(loanType))) {
                setValue('has_delay', false)
                setValue('delay_duration', undefined)
                setValue('delay_type', undefined)
            }
        }, [loanType, setValue])

        const selectedLoanTypeLabel = loanTypeOptions.find((opt) => opt.value === loanType)?.label
        const activeAmount = showLeftAmount ? amountLeft : amount
        const summaryParts: string[] = []
        if (rate != null && Number.isFinite(rate)) summaryParts.push(`${String(rate).replace('.', ',')}%`)
        if (activeAmount != null && Number.isFinite(activeAmount))
            summaryParts.push(`${activeAmount.toLocaleString('fr-FR')}${currencySymbol(selectedCurrency)}`)
        if (activeDuration != null && Number.isFinite(activeDuration)) summaryParts.push(`${activeDuration} mois`)
        if (showDelayFields(loanType)) {
            summaryParts.push(
                hasDelay ? (delayType === DELAY_TYPES.PARTIAL ? 'Différé partiel' : 'Différé total') : 'Pas de différé'
            )
        } else if (isInFineLoan(loanType) || isRelayLoan(loanType)) {
            summaryParts.push(
                hasDelay
                    ? delayType === DELAY_TYPES.PARTIAL
                        ? 'Intérêts remboursés partiellement'
                        : 'Intérêts remboursés totalement'
                    : "Pas d'intérêts remboursés"
            )
        }
        const collapsedSummary = summaryParts.join(' • ')
        const stages = watch('stages')
        const collapsedStages = loanHasStages(loanType)
            ? (stages ?? [])
                  .map((stage, index) =>
                      stage.amount != null && stage.month_duration
                          ? `Palier ${index + 1} : ${stage.amount.toLocaleString('fr-FR')}${currencySymbol(selectedCurrency)} • ${stage.month_duration} mois`
                          : null
                  )
                  .filter(Boolean)
                  .join(' / ')
            : ''

        return (
            <div
                className={`loan-comparator-loan-form mb-5 relative rounded-2xl border border-rm-blue bg-[#F4F8FF] ${className}`}
                data-testid="loan-form"
            >
                {canRemove && onRemove && !collapsed && <RemoveLoanButton onRemove={onRemove} />}

                {/* Header dépliable : titre + (replié) pill du type + résumé + chevron */}
                <div
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-4 cursor-pointer"
                    onClick={() => setCollapsed(!collapsed)}
                >
                    <h3
                        style={{ fontFamily: 'var(--font-recoleta)' }}
                        className="order-1 text-[21px] leading-[1.1] font-bold text-pale m-0"
                    >
                        Ligne de prêt N°{loanIndex + 1}
                    </h3>
                    {collapsed && selectedLoanTypeLabel && (
                        <span className="order-2 rounded-full border border-rm-gray-light bg-white px-3 py-0.5 text-[13px] text-pale">
                            {selectedLoanTypeLabel}
                        </span>
                    )}
                    {collapsed && collapsedSummary && (
                        <span className="order-4 w-full text-[14px] text-pale md:order-3 md:w-auto">
                            {collapsedSummary}
                        </span>
                    )}
                    {collapsed && collapsedStages && (
                        <span className="order-5 rounded-full bg-blue-400 px-3 py-0.5 text-[13px] text-pale md:order-4">
                            {collapsedStages}
                        </span>
                    )}
                    {hasImportedField && <LoanImportedBadge className="order-6 md:order-4" />}
                    <button
                        type="button"
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? 'Déplier la ligne de prêt' : 'Replier la ligne de prêt'}
                        onClick={(e) => {
                            e.stopPropagation()
                            setCollapsed(!collapsed)
                        }}
                        className="order-3 ml-auto flex cursor-pointer items-center gap-1.5 text-[14px] text-rm-blue md:order-5"
                    >
                        {collapsed ? 'Voir le détail' : 'Voir moins'}
                        <i
                            className={`fa-regular fa-chevron-down transition-transform duration-300 ${collapsed ? '' : 'rotate-180'}`}
                        />
                    </button>
                </div>

                <div className={`px-5 pb-5 ${collapsed ? 'hidden' : ''}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[30px] gap-y-[15px]">
                        {/* Type de prêt */}
                        <LoanFormField
                            label="Type de prêt*"
                            inline={false}
                            tooltip={{
                                title: 'Type de prêt',
                                text: "<strong>Prêt classique / amortissable :</strong> <u>Il s'agit du type de prêt le plus fréquent</u> : le capital emprunté est remboursé tout au long du prêt.<br><strong>Prêt in fine :</strong> Le capital emprunté est remboursé à la fin du prêt.<br><strong>Prêt relais :</strong> s'apparente au prêt in fine mais sur une période plus courte de 36 mois maximum.<br><strong>Prêt à paliers :</strong> Selon différentes mensualités avec des paliers de remboursement différents.<br><strong>Prêt construction (VEFA, travaux ...) :</strong> Le crédit est débloqué progressivement pour acheter un bien en construction.",
                            }}
                            tooltipId={`loan-type-tooltip-${loanIndex}`}
                            error={errors.type}
                            imported={isFieldImported('type')}
                        >
                            <AppSelect
                                label=""
                                options={loanTypeOptions}
                                placeholder="Sélectionner..."
                                variant="v2"
                                {...register('type', { valueAsNumber: true })}
                            />
                        </LoanFormField>

                        {/* Taux d'intérêt */}
                        <LoanFormField
                            label="Taux d'intérêt (en %)*"
                            chatPrefill={chatMark('rate')}
                            inline={false}
                            tooltip={{
                                title: "Taux d'intérêt",
                                text: "Taux d'intérêt <u>hors assurance</u>.<br><strong>NB : </strong> Si votre emprunt est fixé sur un taux d'intérêt variable, mettez le taux d'intérêt actuel et nous intégrerons cela dans un second temps.",
                            }}
                            tooltipId={`loan-rate-tooltip-${loanIndex}`}
                            error={errors.rate}
                            imported={isFieldImported('rate')}
                        >
                            <Controller
                                name="rate"
                                control={control}
                                render={({ field }) => (
                                    <AppNumericInput
                                        fieldName={field.name}
                                        value={field.value ?? ''}
                                        borders="neutral"
                                        patternFormat="#,##"
                                        placeholder="Ex. 3,5"
                                        onChange={({ value }) => field.onChange(parseFormattedAmount(value))}
                                        onBlur={field.onBlur}
                                    />
                                )}
                            />
                        </LoanFormField>

                        {!isNewLoan && (
                            <LoanFormField
                                label="Vous connaissez*"
                                row="switch"
                                labelId={`amount-mode-label-${loanIndex}`}
                                className="md:col-span-2"
                            >
                                <YesNoSwitch
                                    labels={['Le capital initial', 'Le capital restant dû']}
                                    // Sélecteur de mode, pas une question : inverser depuis le mode actif
                                    // reconvertirait le différé et le capital restant sur un clic involontaire.
                                    invertOnActiveClick={false}
                                    value={!showLeftAmount}
                                    onChange={(isInitial) => setAmountFieldMode(!isInitial)}
                                    ariaLabelledBy={`amount-mode-label-${loanIndex}`}
                                    testIdPrefix={`amount-mode-${loanIndex}`}
                                    widthClassName="md:w-[340px]"
                                    className="[--rm-switch-track:#fff]"
                                />
                            </LoanFormField>
                        )}

                        {/* Montant emprunté */}
                        <LoanFormField
                            label={`${showLeftAmount ? 'Capital restant' : isNewLoan ? 'Montant emprunté' : 'Montant initial'} (en ${currencySymbol(selectedCurrency)})*`}
                            chatPrefill={chatMark('loanAmount')}
                            inline={false}
                            tooltip={{
                                title: showLeftAmount ? 'Capital restant' : 'Montant initial',
                                text: showLeftAmount
                                    ? 'Montant restant du prêt à rembourser.'
                                    : isNewLoan
                                      ? 'Montant emprunté auprès de votre banque.'
                                      : 'Montant initial du prêt. Le capital restant dû à assurer est <u>calculé automatiquement</u> par notre outil.',
                            }}
                            tooltipId={`loan-amount-tooltip-${loanIndex}`}
                            error={
                                (showLeftAmount ? errors.amount_left : errors.amount) ??
                                (externalAmountError ? { message: externalAmountError } : undefined)
                            }
                            imported={isFieldImported('amount')}
                        >
                            <Controller
                                name={showLeftAmount ? 'amount_left' : 'amount'}
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <AppNumericInput
                                            fieldName={field.name}
                                            value={field.value ?? ''}
                                            borders="neutral"
                                            thousandSeparator=" "
                                            placeholder="Ex. 250 000"
                                            addonTheme="blank"
                                            addonContent={
                                                <CurrencySwitch
                                                    value={selectedCurrency}
                                                    onChange={(unit) =>
                                                        setValue(
                                                            'selected_currency',
                                                            unit as (typeof CURRENCIES)[keyof typeof CURRENCIES]
                                                        )
                                                    }
                                                />
                                            }
                                            onChange={({ value }) => field.onChange(parseFormattedAmount(value))}
                                            onBlur={field.onBlur}
                                        />
                                    )
                                }}
                            />
                        </LoanFormField>

                        {/* Durée totale */}
                        <LoanFormField
                            label={`${showLeftAmount ? 'Durée restante' : isNewLoan ? 'Durée totale' : 'Durée initiale'} (en mois - Différé compris)*`}
                            chatPrefill={chatMark('remainingMonths')}
                            inline={false}
                            tooltip={{
                                title: showLeftAmount
                                    ? 'Durée restante'
                                    : isNewLoan
                                      ? 'Durée totale'
                                      : 'Durée initiale',
                                text: showLeftAmount
                                    ? "Durée restante du prêt en mois. Elle comprend le différé lorsqu'il y en a un."
                                    : isNewLoan
                                      ? "Durée totale du prêt en mois. Elle comprend le <strong>différé de remboursement</strong> lorsqu'il y en a un."
                                      : "Durée initiale du prêt en mois. Elle comprend le <strong>différé de remboursement</strong> lorsqu'il y en a un.",
                            }}
                            tooltipId={`loan-duration-tooltip-${loanIndex}`}
                            error={showLeftAmount ? errors.duration_left : errors.duration}
                            imported={isFieldImported('duration')}
                        >
                            {/* key : duration et duration_left gardent chacun leur node DOM, sinon les deux refs RHF pointent le même input et le recalcul de duration_left écrase la saisie */}
                            <div className="relative">
                                <AppFormInput
                                    key={showLeftAmount ? 'duration_left' : 'duration'}
                                    label=""
                                    type="number"
                                    inputMode="numeric"
                                    min={loanValidation.duration.min}
                                    max={loanValidation.duration.max}
                                    placeholder="Ex. 20"
                                    variant="loan"
                                    className="pr-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    onInput={(e) => {
                                        const target = e.target as HTMLInputElement
                                        if (target.value.length > 3) target.value = target.value.slice(0, 3)
                                    }}
                                    {...register(showLeftAmount ? 'duration_left' : 'duration', {
                                        valueAsNumber: true,
                                    })}
                                />
                                {Number.isFinite(activeDuration) && activeDuration! >= 12 && (
                                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-blue-500">
                                        ={formatDurationToYearsAndMonths(activeDuration!)}
                                    </span>
                                )}
                            </div>
                        </LoanFormField>
                    </div>

                    {showDelayFields(loanType) && (
                        <div className="mt-[15px]">
                            <LoanFormField
                                label={`${isNewLoan ? 'Différé de remboursement' : showLeftAmount ? 'Différé de remboursement restant' : 'Différé de remboursement initial'}*`}
                                inline={false}
                            >
                                <p className="mb-2 flex items-center gap-2 text-[13px] text-pale/80">
                                    Votre remboursement commence-t-il immédiatement ?
                                    {isFieldImported('delay') && <LoanImportedBadge />}
                                </p>
                                <div className={isFieldImported('delay') ? 'loan-field-imported' : ''}>
                                    <Controller
                                        name="delay_type"
                                        control={control}
                                        render={({ field }) => (
                                            <DescriptiveToggle
                                                options={delayToggleOptions}
                                                value={hasDelay ? (field.value ?? DELAY_TYPES.PARTIAL) : 'none'}
                                                onChange={(value) => {
                                                    if (value === 'none') {
                                                        setValue('has_delay', false)
                                                        setValue('delay_duration', undefined)
                                                        field.onChange(undefined)
                                                    } else {
                                                        setValue('has_delay', true)
                                                        field.onChange(value)
                                                    }
                                                }}
                                                columns={3}
                                                selectedTone="plain"
                                                ariaLabel="Différé de remboursement"
                                            />
                                        )}
                                    />
                                </div>
                            </LoanFormField>

                            {hasDelay && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                    <LoanFormField label="Durée du différé (en mois)*" error={errors.delay_duration}>
                                        <div className="relative">
                                            <AppFormInput
                                                label=""
                                                type="number"
                                                inputMode="numeric"
                                                min={1}
                                                max={LOAN_VALIDATION.MAX_DELAY_MONTHS}
                                                placeholder="0"
                                                variant="loan"
                                                className="pr-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                onInput={(e) => {
                                                    const target = e.target as HTMLInputElement
                                                    if (target.value.length > 3) target.value = target.value.slice(0, 3)
                                                }}
                                                {...register('delay_duration', { valueAsNumber: true })}
                                            />
                                            {Number.isFinite(delayDuration) && delayDuration! >= 12 && (
                                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-blue-500">
                                                    ={formatDurationToYearsAndMonths(delayDuration!)}
                                                </span>
                                            )}
                                        </div>
                                    </LoanFormField>
                                </div>
                            )}
                        </div>
                    )}

                    {/* In Fine / Relay specific field */}
                    {(isInFineLoan(loanType) || isRelayLoan(loanType)) && (
                        <div className="mt-[15px]">
                            <LoanFormField label="Intérêts remboursés*" inline={false}>
                                <p className="mb-2 flex items-center gap-2 text-[13px] text-pale/80">
                                    Remboursez vous des intérêts pendant votre prêt ?
                                    {isFieldImported('delay') && <LoanImportedBadge />}
                                </p>
                                <div className={isFieldImported('delay') ? 'loan-field-imported' : ''}>
                                    <Controller
                                        name="delay_type"
                                        control={control}
                                        render={({ field }) => (
                                            <DescriptiveToggle
                                                options={interestsToggleOptions}
                                                value={hasDelay ? (field.value ?? DELAY_TYPES.PARTIAL) : 'none'}
                                                onChange={(value) => {
                                                    if (value === 'none') {
                                                        setValue('has_delay', false)
                                                        setValue('delay_duration', undefined)
                                                        field.onChange(undefined)
                                                    } else {
                                                        setValue('has_delay', true)
                                                        field.onChange(value)
                                                    }
                                                }}
                                                columns={3}
                                                selectedTone="plain"
                                                ariaLabel="Intérêts remboursés"
                                            />
                                        )}
                                    />
                                </div>
                            </LoanFormField>

                            {hasDelay && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                    <LoanFormField
                                        label="Durée du remboursement (en mois)*"
                                        error={errors.delay_duration}
                                    >
                                        <AppFormInput
                                            label=""
                                            type="number"
                                            inputMode="numeric"
                                            min={1}
                                            placeholder="0"
                                            variant="loan"
                                            showClearButton
                                            onClear={() => {
                                                setValue('has_delay', false)
                                                setValue('delay_duration', undefined)
                                                setValue('delay_type', undefined)
                                            }}
                                            onInput={(e) => {
                                                const target = e.target as HTMLInputElement
                                                if (target.value.length > 3) target.value = target.value.slice(0, 3)
                                            }}
                                            {...register('delay_duration', { valueAsNumber: true })}
                                        />
                                    </LoanFormField>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Stages Section (for STAGE and BUILDING loan types) */}
                    {loanHasStages(loanType) && (
                        <div className={isFieldImported('stages') ? 'loan-field-imported' : ''}>
                            <LoanComparatorPalierForm
                                key={`palier-${loanData?.id}-${loanData?.palier?.length || 0}`}
                                loanIndex={loanIndex}
                                loanType={loanType}
                                loanAmount={showLeftAmount ? watch('amount_left') : watch('amount')}
                                loanDuration={
                                    showLeftAmount ? (watch('duration_left') ?? undefined) : watch('duration')
                                }
                                loanRate={watch('rate') ?? 0}
                                delayDuration={hasDelay ? delayDuration : 0}
                                currencySymbol={currencySymbol(selectedCurrency)}
                                initialStages={loanData?.palier}
                                onStagesChange={handleStagesChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    }
)

LoanComparatorLoanForm.displayName = 'LoanComparatorLoanForm'

function RemoveLoanButton({ onRemove }: { onRemove: () => void }) {
    const [showText, setShowText] = useState(false)

    return (
        <button
            type="button"
            className="absolute right-[140px] top-4 whitespace-nowrap bg-red-50 text-red-600 hover:bg-red-100 border-none font-bold rounded-lg px-2 py-1 inline-flex items-center gap-1 text-[14px] transition-all duration-500 z-10"
            onMouseEnter={() => setShowText(true)}
            onMouseLeave={() => setShowText(false)}
            onClick={onRemove}
        >
            {showText && <span className="transition-all duration-300">Supprimer la ligne de prêt</span>}
            <i className="fa-regular fa-trash-alt" />
        </button>
    )
}

export default LoanComparatorLoanForm
